import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { useSetAtom } from 'jotai';
import { useResetAtom } from 'jotai/utils';
import { Box, Typography } from '@mui/material';
import { Country } from '../../typings';
import { parsePhoneNumber } from '../../request/validate-phone';
import { getCountryFromSymbol } from '../../../../utils/countries-mapper';
import { verifyOtpUseCase } from '../../request/verify-otp-use-case';
import {
  LoginStepAtom,
  phoneNumberAtom,
  userSignupDetailsAtom,
} from '../../state';
import { useSetAuth } from '../../../../hooks/useAuth';
import useNotifications from '../../../../hooks/useNotifications';
import { logoutUseCase } from '../../../../request/getLogout';
import { userIsLoggedIn } from '../../../../infra/auth/firebase-auth';
import { ROUTES } from '../../../../routes/auth-routes/routes';
import Loader from '../../../../components/loader';
import PageWrapper from '../PageWrapper';

const AutoLogin = () => {
  const { phoneNumber, otp } = useParams<{
    phoneNumber: string;
    otp: string;
  }>();

  const [country, setCountry] = useState<Country>();
  const [phone, setPhone] = useState<string>('');
  const [isReady, setIsReady] = useState(false);

  const resetUserDetails = useResetAtom(userSignupDetailsAtom);
  const resetPhoneNumber = useResetAtom(phoneNumberAtom);
  const setLoginStep = useSetAtom(LoginStepAtom);
  const setPhoneNumber = useSetAtom(phoneNumberAtom);
  const setAuthState = useSetAuth();
  const navigate = useNavigate();
  const { addNotification } = useNotifications();

  const navigateToHome = useCallback(() => {
    navigate(ROUTES.loginPage, { replace: true });
  }, [navigate]);

  const handleLogin = useCallback(async () => {
    if (!otp || !phoneNumber || !country) return;
    try {
      if (userIsLoggedIn()) {
        await logoutUseCase();
        setAuthState({
          isAuthReady: false,
          isCheckingAuth: false,
          isOrgSelected: false,
          isIntegrationEnabled: false,
          isPermissionsReady: false,
          makePayment: false,
        });
      }
      await verifyOtpUseCase({
        setAuthState,
        navigate,
        otp,
        country,
        phone,
        resetPhoneNumber,
        resetUserDetails,
        setLoginStep,
        setPhoneNumber,
        addNotification,
      });
    } catch {
      navigateToHome();
    }
  }, [
    addNotification,
    country,
    navigate,
    navigateToHome,
    otp,
    phone,
    phoneNumber,
    resetPhoneNumber,
    resetUserDetails,
    setAuthState,
    setLoginStep,
    setPhoneNumber,
  ]);

  useEffect(() => {
    if (!phoneNumber) {
      navigateToHome();
      return;
    }
    const { countryCode, phone: nationalNumber } =
      parsePhoneNumber(phoneNumber);
    if (!countryCode || !nationalNumber) {
      navigateToHome();
      return;
    }
    setPhone(nationalNumber.toString());
    const parsedCountry = getCountryFromSymbol(`+${countryCode.toString()}`);
    if (!parsedCountry) {
      navigateToHome();
      return;
    }
    setCountry(parsedCountry);
    setIsReady(true);
  }, [navigateToHome, phoneNumber]);

  useEffect(() => {
    if (isReady) {
      handleLogin();
    }
  }, [handleLogin, isReady]);

  return (
    <PageWrapper showLogout={false}>
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Loader
          size={40}
          loaderProps={{ sx: { color: 'rgb(47, 166, 113)' } }}
          secondary={
            <Typography
              sx={{
                fontSize: '15px',
                fontWeight: 500,
                color: '#838383',
                mt: 2,
              }}
            >
              Logging you in...
            </Typography>
          }
        />
      </Box>
    </PageWrapper>
  );
};

export default AutoLogin;
